import { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { METHOD } from "@/lib/content";

const NOISE = "M0 80 L20 62 L35 96 L52 48 L70 88 L86 40 L104 92 L120 58 L138 100 L156 44 L172 84 L190 52 L208 98 L226 60 L244 90 L262 46 L280 86 L298 64 L316 94 L334 50 L352 82 L370 56 L388 92 L400 70";
const SIGNAL = "M0 110 C80 108 140 100 200 84 S320 40 400 14";

const SignalChart = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.5 });
  const loop = { repeat: Infinity, repeatDelay: 3, delay: 0.8 };
  return (
    <div ref={ref}>
      <svg viewBox="0 0 400 130" className="h-auto w-full" aria-hidden="true" data-testid="signal-chart" data-active={inView ? "true" : "false"}>
        <motion.path d={NOISE} fill="none" stroke="#52525b" strokeWidth="1.5"
          initial={{ pathLength: 0 }} animate={inView ? { pathLength: 1, opacity: [0.9, 0.35, 0.9] } : { pathLength: 0 }}
          transition={{ pathLength: { duration: 1.4, ease: "easeInOut" }, opacity: { duration: 2.4, repeat: Infinity, ease: "easeInOut" } }} />
        <motion.path d={SIGNAL} fill="none" stroke="#0055FF" strokeWidth="3" strokeLinecap="round"
          initial={{ pathLength: 0 }} animate={inView ? { pathLength: [0, 1] } : { pathLength: 0 }}
          transition={inView ? { duration: 1.6, ease: [0.22, 1, 0.36, 1], ...loop } : { duration: 0.2 }} />
        <motion.circle cx="400" cy="14" r="5" fill="#0055FF" initial={{ opacity: 0 }}
          animate={inView ? { opacity: [0, 0, 1, 1, 0], scale: [0.4, 0.4, 1.3, 1, 1] } : { opacity: 0 }}
          transition={inView ? { duration: 4.6, times: [0, 0.33, 0.4, 0.92, 1], repeat: Infinity, delay: 0.8 } : { duration: 0.2 }}
          style={{ transformOrigin: "400px 14px" }} />
      </svg>
    </div>
  );
};

export const Method = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 60%"] });
  const fill = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  return (
    <section data-testid="method-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-40">
      <div className="grid gap-14 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <p className="eyebrow">The noise-free profit finder</p>
            <h2 className="font-display mt-5 text-4xl font-medium leading-tight tracking-tight text-white md:text-5xl">
              Most people follow the noise. <span className="font-serif-i text-zinc-400">You will follow the signal.</span>
            </h2>
            <p className="mt-6 text-zinc-300 md:text-lg">Every chapter is built on one three-part roadmap. Simple enough for a beginner, sharp enough to keep using for years.</p>
            <div className="mt-10 border border-line bg-[#0b0b0b] p-6">
              <div className="mb-3 flex justify-between text-[11px] uppercase tracking-widest text-zinc-400"><span>Market noise</span><span className="text-white">Real signal</span></div>
              <SignalChart />
            </div>
          </div>
        </Reveal>
        <div ref={ref} className="relative lg:col-span-7 lg:pl-10">
          <div className="absolute bottom-0 left-0 top-0 hidden w-px bg-line lg:block">
            <motion.div style={{ height: fill }} className="w-px bg-cta" />
          </div>
          {METHOD.map((m, i) => (
            <Reveal key={m.step} delay={0.05}>
              <article data-testid={`method-step-${i}`} className="border-b border-line py-12 first:pt-0 lg:py-16">
                <p className="eyebrow flex items-center gap-3"><span className="font-display text-white">0{i + 1}</span>{m.step}</p>
                <h3 className="font-display mt-5 text-4xl font-medium tracking-tight text-white md:text-6xl">{m.title}</h3>
                <p className="mt-5 max-w-lg text-base leading-relaxed text-zinc-300 md:text-lg">{m.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
